import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MenuOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import { RankingAPI } from "../../API/RankingAPI";
import { ColumnResizeHandle } from "./ColumnResize";
import { toApiColumnLayoutKey } from "./gridPreferencesUtils";

const HEADER_HINT_CONTENT_STYLE = {
  fontWeight: 500,
  color: "#565656",
  fontSize: "15px",
  lineHeight: "1.5rem",
  margin: 0,
};

// Only the drag-handle column is non-draggable. Status / Comments / Assigned To
// are now reorderable via the header, like every other managed column.
export const NON_DRAGGABLE_COLUMN_KEYS = new Set(["sort"]);

const ColumnDragContext = createContext(null);

export const ColumnDragProvider = ({ value, children }) => (
  <ColumnDragContext.Provider value={value}>{children}</ColumnDragContext.Provider>
);

const getColumnKey = (column, rest) =>
  column?.key || rest["data-column-key"] || column?.dataIndex;

export const getColumnHeaderCellProps = (columnId) => ({
  "data-column-key": columnId,
});

const DraggableHeaderCell = ({
  columnKey,
  children,
  className,
  onResizeMouseDown,
  ...rest
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnKey });

  const style = {
    ...rest.style,
    transform: CSS.Transform.toString(transform),
    transition,
    userSelect: "none",
    ...(isDragging
      ? {
          zIndex: 2,
          background: "#fafafa",
          position: "relative",
        }
      : {}),
  };

  return (
    <th
      {...rest}
      ref={setNodeRef}
      style={style}
      className={`dnd-column-header${className ? ` ${className}` : ""}${
        isDragging ? " dnd-column-header--dragging" : ""
      }`}
    >
      <span className="dnd-column-header-content">
        <Popover
          title=""
          content={
            <p style={HEADER_HINT_CONTENT_STYLE}>Drag to reorder column</p>
          }
        >
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="dnd-column-header-grip field-item-grip-btn"
            aria-label="Drag to reorder column"
            {...attributes}
            {...listeners}
          >
            <MenuOutlined
              style={{
                touchAction: "none",
              }}
            />
          </button>
        </Popover>
        <span className="dnd-column-header-label-box">
          <span className="dnd-column-header-label">{children}</span>
        </span>
        {typeof onResizeMouseDown === "function" && (
          <ColumnResizeHandle onMouseDown={onResizeMouseDown} />
        )}
      </span>
    </th>
  );
};

const StableHeaderCell = ({ column, children, onResizeMouseDown, ...rest }) => {
  const ctx = useContext(ColumnDragContext);
  const columnKey = getColumnKey(column, rest);

  if (
    !ctx ||
    !columnKey ||
    NON_DRAGGABLE_COLUMN_KEYS.has(columnKey) ||
    !ctx.sortableIdSet.has(columnKey)
  ) {
    return (
      <th
        {...rest}
        className={`dnd-column-header dnd-column-header--stable${
          rest.className ? ` ${rest.className}` : ""
        }`}
      >
        {typeof onResizeMouseDown === "function" ? (
          <>
            <span className="dnd-column-header-content">
              <span className="dnd-column-header-label">{children}</span>
            </span>
            <ColumnResizeHandle onMouseDown={onResizeMouseDown} />
          </>
        ) : (
          children
        )}
      </th>
    );
  }

  return (
    <DraggableHeaderCell
      columnKey={columnKey}
      onResizeMouseDown={onResizeMouseDown}
      {...rest}
    >
      {children}
    </DraggableHeaderCell>
  );
};

const StableHeaderWrapper = (props) => {
  const ctx = useContext(ColumnDragContext);
  if (!ctx) {
    return <thead {...props} />;
  }

  return (
    <DndContext
      id="ranking-column-dnd"
      sensors={ctx.sensors}
      collisionDetection={closestCenter}
      autoScroll={false}
      onDragStart={ctx.onDragStart}
      onDragEnd={ctx.onDragEnd}
      modifiers={[restrictToHorizontalAxis]}
    >
      <SortableContext
        items={ctx.sortableColumnIds}
        strategy={horizontalListSortingStrategy}
      >
        <thead {...props} />
      </SortableContext>
    </DndContext>
  );
};

export const stableColumnHeaderComponents = {
  wrapper: StableHeaderWrapper,
  cell: StableHeaderCell,
};

export const useColumnDrag = ({
  selectedColumns = [],
  setSelectedColumns,
  messageToast,
  onColumnDragStart,
  onColumnReorder,
  isColumnFrozen,
}) => {
  const columnDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
  );

  const sortableColumnIds = useMemo(
    () => selectedColumns.map((col) => col.id),
    [selectedColumns],
  );

  const sortableIdSet = useMemo(
    () => new Set(sortableColumnIds),
    [sortableColumnIds.join(",")],
  );

  const selectedColumnsRef = useRef(selectedColumns);
  selectedColumnsRef.current = selectedColumns;

  const onColumnDragStartRef = useRef(onColumnDragStart);
  onColumnDragStartRef.current = onColumnDragStart;

  const onColumnReorderRef = useRef(onColumnReorder);
  onColumnReorderRef.current = onColumnReorder;

  const isColumnFrozenRef = useRef(isColumnFrozen);
  isColumnFrozenRef.current = isColumnFrozen;

  const handleDragStart = useCallback(() => {
    onColumnDragStartRef.current?.();
  }, []);

  const handleDragEnd = useCallback(
    async ({ active, over }) => {
      if (!setSelectedColumns || !over || active.id === over.id) return;

      const currentColumns = selectedColumnsRef.current;
      const oldIndex = currentColumns.findIndex((col) => col.id === active.id);
      const newIndex = currentColumns.findIndex((col) => col.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Frozen columns render as a contiguous block at the left of the grid, so
      // ignore a drop that crosses the frozen / unfrozen boundary.
      const isFrozen = isColumnFrozenRef.current;
      if (isFrozen && isFrozen(active.id) !== isFrozen(over.id)) {
        return;
      }

      const reordered = arrayMove([...currentColumns], oldIndex, newIndex);
      const updatedColumns = reordered.map((col, idx) => ({
        ...col,
        rank: idx + 1,
      }));
      const previousColumns = currentColumns;

      onColumnReorderRef.current?.();
      setSelectedColumns(updatedColumns);

      const bodyData = {
        columns: updatedColumns.map((col) => ({
          // Map the grid key back to the API key (comments -> have_comments).
          column_key: toApiColumnLayoutKey(col.id),
          display_order: col.rank,
        })),
      };

      try {
        const response = await RankingAPI.updateColumnConfig(bodyData);
        if (response?.data?.status === true || response?.status === true) {
          messageToast("success", "Column configuration updated successfully.");
        } else {
          onColumnReorderRef.current?.();
          setSelectedColumns(previousColumns);
          messageToast("error", "Failed to update column order.");
        }
      } catch {
        onColumnReorderRef.current?.();
        setSelectedColumns(previousColumns);
        messageToast("error", "Failed to update column configuration.");
      }
    },
    [setSelectedColumns, messageToast],
  );

  const contextValue = useMemo(
    () => ({
      sensors: columnDragSensors,
      sortableColumnIds,
      sortableIdSet,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
    }),
    [
      columnDragSensors,
      sortableColumnIds,
      sortableIdSet,
      handleDragStart,
      handleDragEnd,
    ],
  );

  return { contextValue };
};

// Hoisted so useSensor / useSensors actually memoize. Inline object literals
// build a new sensor descriptor every render, which changed BodyWrapper's
// identity below. Same for the modifiers array.
const ROW_POINTER_ACTIVATION = { activationConstraint: { distance: 5 } };
const ROW_DRAG_MODIFIERS = [restrictToVerticalAxis];

export const useRowDrag = ({ rowKeys = [], onDragEnd }) => {
  const rowDragSensors = useSensors(
    useSensor(PointerSensor, ROW_POINTER_ACTIVATION),
  );

  // BodyWrapper is the <tbody> component *type*. A new identity makes React
  // unmount and remount every row, so onDragEnd is read through a ref instead
  // of being a dependency — only a real row-set change rebuilds the wrapper.
  // Same drag behaviour, just no longer re-created on unrelated renders.
  const onDragEndRef = useRef(onDragEnd);
  onDragEndRef.current = onDragEnd;

  const handleDragEnd = useCallback(
    (event) => onDragEndRef.current?.(event),
    [],
  );

  const BodyWrapper = useCallback(
    (props) => (
      <DndContext
        id="ranking-row-dnd"
        sensors={rowDragSensors}
        modifiers={ROW_DRAG_MODIFIERS}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={rowKeys}
          strategy={verticalListSortingStrategy}
        >
          <tbody {...props} />
        </SortableContext>
      </DndContext>
    ),
    [rowDragSensors, handleDragEnd, rowKeys],
  );

  return { BodyWrapper };
};
